import { reactive, toRefs } from 'vue';

interface IConfirmOptions {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  maxWidth?: string;
}

interface IConfirmResult {
  confirmed: boolean;
}

interface IConfirmState {
  isVisible: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  maxWidth: string;
  resolver: ((result: IConfirmResult) => void) | null;
}

// Global state - shared across all instances of useHConfirm
const confirmState = reactive<IConfirmState>({
  isVisible: false,
  title: '',
  message: '',
  confirmText: '',
  cancelText: '',
  maxWidth: '400',
  resolver: null,
});

/**
 * A Vue composable that provides a programmatic confirmation dialog similar to JavaScript's confirm().
 *
 * @function
 * @name useHConfirm
 * @description
 * This composable provides a method to show a confirmation dialog programmatically.
 * It returns a promise that resolves when the user confirms or cancels the dialog.
 *
 * This uses a global reactive state pattern, so you need to include the HConfirmationDialog
 * component in your root app layout (e.g., App.vue) and bind it to the confirm state.
 *
 * Usage:
 * ```typescript
 * // In your root App.vue or layout component:
 * import { useHConfirmState } from '@onify/helix-core';
 * const confirmState = useHConfirmState();
 *
 * <h-confirmation-dialog
 *   v-model="confirmState.isVisible"
 *   :title="confirmState.title"
 *   :message="confirmState.message"
 *   :confirm-text="confirmState.confirmText"
 *   :cancel-text="confirmState.cancelText"
 *   :max-width="confirmState.maxWidth"
 *   @confirm="confirmState.handleConfirm"
 *   @cancel="confirmState.handleCancel"
 * />
 *
 * // In any component:
 * const { confirm } = useHConfirm();
 *
 * const result = await confirm({
 *   title: 'Delete Item',
 *   message: 'Are you sure you want to delete this item?'
 * });
 * if (result.confirmed) {
 *   // User confirmed
 * }
 * ```
 *
 * @returns {Object} An object containing:
 * - `confirm`: A function that shows a confirmation dialog and returns a promise with the result
 */
export function useHConfirm() {
  const confirm = async (options: IConfirmOptions = {}): Promise<IConfirmResult> => {
    return new Promise((resolve) => {
      // Update state with new options
      confirmState.title = options.title || '';
      confirmState.message = options.message || '';
      confirmState.confirmText = options.confirmText || '';
      confirmState.cancelText = options.cancelText || '';
      confirmState.maxWidth = options.maxWidth || '400';
      confirmState.resolver = resolve;
      confirmState.isVisible = true;
    });
  };

  return {
    confirm,
  };
}

/**
 * Hook for the root component to access the confirm state and handlers
 * @returns Confirm state properties and handler functions
 */
export function useHConfirmState() {
  const handleConfirm = () => {
    if (confirmState.resolver) {
      confirmState.resolver({ confirmed: true });
      confirmState.resolver = null;
    }
    confirmState.isVisible = false;
  };

  const handleCancel = () => {
    if (confirmState.resolver) {
      confirmState.resolver({ confirmed: false });
      confirmState.resolver = null;
    }
    confirmState.isVisible = false;
  };

  // Use toRefs to maintain reactivity
  const { isVisible, title, message, confirmText, cancelText, maxWidth } = toRefs(confirmState);

  return {
    isVisible,
    title,
    message,
    confirmText,
    cancelText,
    maxWidth,
    handleConfirm,
    handleCancel,
  };
}
